import type { EmailGroupT } from "~/types"

export const useOrganizationsStore = defineStore("organizations", () => {
    const loading = ref(true)
    const map = ref({} as Record<string, EmailGroupT>)
    const items = computed(() => Object.values(map.value))
    const selectedOrgAlias = ref<string | null>(null)
    const selectedOrg = computed(() => selectedOrgAlias.value ? map.value[selectedOrgAlias.value] : null)
    const emailsStore = useEmailsStore()
    const userOrganizations = ref<EmailGroupT[]>([])

    // Fetch user's organizations with permissions
    const fetchUserOrganizations = async () => {
        loading.value = true
        try {
            const data = await $fetch("/api/v1/organizations/user")

            if (!data || !Array.isArray(data)) {
                console.error("Failed to fetch user organizations")
                return
            }

            userOrganizations.value = data

            // Process and map organizations
            data.forEach((item: EmailGroupT) => {
                if (!item.alias) {
                    console.error("Missing alias in item", item)
                    return
                }

                // Add computed property for emails
                item.emails = computed(() => {
                    return emailsStore.items.filter((email) => {
                        return email.parent === item.alias
                    })
                })

                map.value[item.alias] = item
            })

            // A remembered workspace the user can no longer see is no selection.
            if (selectedOrgAlias.value && !map.value[selectedOrgAlias.value]) {
                selectedOrgAlias.value = null
            }
            // With one workspace there is nothing to choose. With several, the
            // picker page asks (see useRequireWorkspace).
            if (!selectedOrgAlias.value && data.length === 1) {
                selectedOrgAlias.value = data[0].alias
            }
        } catch (error) {
            console.error("Failed to fetch user organizations", error)
        } finally {
            loading.value = false
        }
    }

    // Get detailed info about an organization
    const fetchOrgDetails = async (alias: string) => {
        // Validate alias is a valid string
        if (!alias || typeof alias !== "string") {
            console.error("Invalid organization alias provided to fetchOrgDetails:", alias)
            return null
        }

        try {
            const details = await $fetch(`/api/v1/organizations/${alias}/details`)

            if (details && map.value[alias]) {
                // Update the organization with detailed info
                map.value[alias] = {
                    ...map.value[alias],
                    ...details,
                    // Preserve computed emails property
                    emails: map.value[alias].emails
                }
            }

            return details
        } catch (error) {
            console.error(`Failed to fetch organization details for ${alias}:`, error)
            return null
        }
    }

    // Get organization by alias
    const byAlias = async (alias: string) => {
        if (!map.value[alias]) {
            try {
                const data = await $fetch(`/api/v1/organizations/${alias}`)
                if (data) {
                    data.emails = computed(() => {
                        return emailsStore.items.filter((email) => {
                            return email.parent === alias
                        })
                    })
                    map.value[alias] = data
                }
            } catch (error) {
                console.error(`Failed to fetch organization ${alias}:`, error)
            }
        }
        return map.value[alias]
    }

    // Set the selected organization
    const selectOrganization = (alias: any) => {
        // Handle potential object values from UI components
        let aliasStr: string

        if (typeof alias === "object" && alias !== null) {
            // Try to extract a string alias from a potential object
            if (alias.alias && typeof alias.alias === "string") {
                aliasStr = alias.alias
                console.log(`Converted object to string alias: ${aliasStr}`)
            } else if (alias.value && typeof alias.value === "string") {
                aliasStr = alias.value
                console.log(`Extracted string alias from value property: ${aliasStr}`)
            } else {
                console.error("Invalid organization object:", alias)
                return
            }
        } else if (typeof alias === "string") {
            aliasStr = alias
        } else {
            console.error("Invalid organization alias, expected string or object:", alias)
            return
        }

        // Now proceed with the string alias
        if (map.value[aliasStr]) {
            console.log(`Setting organization alias in store: ${aliasStr}`)
            selectedOrgAlias.value = aliasStr
            // Fetch detailed info for the selected org
            fetchOrgDetails(aliasStr)
        } else {
            console.error(`Organization with alias ${aliasStr} not found in available organizations`)
        }
    }

    // Initialize the store if needed
    const hydrated = ref(false)

    // Only fetch organizations if we don't have them already (e.g., from localStorage).
    // Signed out there is nothing to fetch: the request would be a 401 on every public
    // page (landing, login, client review). The user store fetches once a session exists.
    if (!useSupabaseUser().value) {
        loading.value = false
    } else if (userOrganizations.value.length === 0 || !selectedOrgAlias.value) {
        fetchUserOrganizations()
    } else {
        console.log("Using persisted organization data")
        loading.value = false
        hydrated.value = true

        // Make sure the map is populated with the stored user organizations
        userOrganizations.value.forEach(org => {
            if (org.alias) {
                // Add computed property for emails if not already there
                if (!map.value[org.alias]) {
                    org.emails = computed(() => {
                        return emailsStore.items.filter((email) => {
                            return email.parent === org.alias
                        })
                    })
                    map.value[org.alias] = org
                }
            }
        })

        // If we have a selected org, fetch its details
        if (selectedOrgAlias.value && map.value[selectedOrgAlias.value]) {
            fetchOrgDetails(selectedOrgAlias.value)
        }
    }

    return {
        loading,
        map,
        items,
        selectedOrgAlias,
        selectedOrg,
        userOrganizations,
        hydrated,
        fetchUserOrganizations,
        fetchOrgDetails,
        byAlias,
        selectOrganization
    }
}, {
    persist: {
        storage: localStorage,
        paths: ["selectedOrgAlias", "userOrganizations"],
        serializer: {
            deserialize: (value) => {
                const data = JSON.parse(value)
                // Ensure selectedOrgAlias is a string
                if (data.selectedOrgAlias && typeof data.selectedOrgAlias !== "string") {
                    console.warn("Invalid selectedOrgAlias found in storage:", data.selectedOrgAlias)
                    data.selectedOrgAlias = null
                }
                return data
            },
            serialize: (value) => {
                // Create a copy to avoid modifying the original store state
                const data = JSON.parse(JSON.stringify(value))

                // Handle potentially complex selectedOrgAlias
                if (data.selectedOrgAlias) {
                    if (typeof data.selectedOrgAlias === "string") {
                        // Keep string values
                    } else if (typeof data.selectedOrgAlias === "object" && data.selectedOrgAlias !== null) {
                        // Extract string value from object
                        if (data.selectedOrgAlias.alias && typeof data.selectedOrgAlias.alias === "string") {
                            data.selectedOrgAlias = data.selectedOrgAlias.alias
                        } else if (data.selectedOrgAlias.value && typeof data.selectedOrgAlias.value === "string") {
                            data.selectedOrgAlias = data.selectedOrgAlias.value
                        } else {
                            // Fallback - set to null if we can't extract a valid string
                            console.warn("Could not extract string alias from object:", data.selectedOrgAlias)
                            data.selectedOrgAlias = null
                        }
                    } else {
                        // For any other types, try string conversion or null
                        try {
                            data.selectedOrgAlias = String(data.selectedOrgAlias)
                        } catch (e) {
                            data.selectedOrgAlias = null
                        }
                    }
                }

                // Also sanitize organization objects to ensure they're serializable
                if (data.userOrganizations && Array.isArray(data.userOrganizations)) {
                    data.userOrganizations = data.userOrganizations.map(org => {
                        // Remove any computed properties that can't be serialized
                        const {emails, ...rest} = org
                        return rest
                    })
                }

                return JSON.stringify(data)
            }
        },
        beforeRestore: (ctx) => {
            console.log("Before restoring organizations state")
        },
        afterRestore: (ctx) => {
            console.log("Organizations state restored")
        }
    }
})
